import React, { useContext } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { groq } from "next-sanity";
import { getClient } from "lib/sanity.server";
import BlockContent from "@sanity/block-content-to-react";
import { Heading, HStack, Text } from "@chakra-ui/react";
import { UserContext } from "src/context/UserContext";
import {
  FooterLogo,
  ScreenContainer,
  BrandTitle,
  PageHead,
} from "src/components";

const postQuery = groq`
*[_type == "textPages" && slug.current == $slug
  || _type == "portalSettings" && _lang == $lang] {
  siteTitle,  
  pageTitle,
  textContent,
  addFooterLogo,
  "slug": slug.current,
  _lang,
  "homeUrlText": pageLinks.homeUrlText,
  "urlText": pageLinks.urlText,
  "pageUrl": pageLinks.pageUrl,
  "logo": uploadLogo.asset->url,
  "logoCaption": uploadLogo.caption,
  titlePartOne,
  titlePartTwo,
}
`;

export default function TextPages({ data }) {
  const router = useRouter();
  const { language } = useContext(UserContext);

  // Filtering out all the keys that have values = null
  if (data !== undefined && data !== "undefined") {
    data.textPage.forEach((obj) => {
      Object.keys(obj).forEach((k) => obj[k] === null && delete obj[k]);
    });
  }

  const {
    addFooterLogo,
    slug,
    siteTitle,
    pageTitle,
    textContent,
    homeUrlText,
    pageUrl,
    urlText,
    titlePartOne,
    titlePartTwo,
    logo,
    logoCaption,
  } =
    data !== "undefined" && data !== undefined
      ? Object.assign({}, data.textPage[0], data.textPage[1])
      : {};

  const serializers = {
    types: {
      block: ({ children }) => {
        return <Text marginBottom="1rem">{children}</Text>;
      },
    },
  };

  if (!router.isFallback && !slug) {
    return <div>Error</div>;
  }

  return (
    <ScreenContainer>
      <PageHead title={`${titlePartOne} ${titlePartTwo} :: ${siteTitle}`} />
      <BrandTitle titlePartOne={titlePartOne} titlePartTwo={titlePartTwo} />
      <Heading as="h1" fontSize="1.2rem">
        {pageTitle}
      </Heading>
      {textContent && (
        <BlockContent
          serializers={serializers}
          blocks={textContent}
          className="block-content"
        />
      )}
      <HStack spacing="6px" justify="center" fontSize="md">
        {homeUrlText && <Link href="/">{homeUrlText}</Link>}
        {pageUrl && urlText && (
          <>
            <Text>|</Text>
            <Link href={`/home${pageUrl}`} locale={language}>
              {urlText}
            </Link>
          </>
        )}
      </HStack>
      {addFooterLogo && <FooterLogo logo={logo} caption={logoCaption} />}
    </ScreenContainer>
  );
}

export async function getStaticProps({ params, preview = false, locale }) {
  const textPage = await getClient(preview).fetch(postQuery, {
    slug: params.slug,
    lang: locale,
  });

  return {
    props: {
      preview,
      data: { textPage },
    },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  const paths = await getClient().fetch(
    groq`*[_type == "textPages" && defined(slug.current) && defined(_lang)][]{ 'params': { 'slug': slug.current }, 'locale': _lang }`
  );

  return {
    paths,
    fallback: true,
  };
}
